import { create } from "zustand";
import {
  STATUS_LIST,
  KanbanDefaultStates,
} from "@/components/ProjectList/CanvasKanban";
import { ProjectStatus } from "@/components/ProjectConfiguration";
import {
  STATE_KEY_TO_STATUS_ID,
  STATUS_TYPE_MAPPING,
} from "@/components/ProjectConfiguration/constants";

interface StatusIconState {
  statusIconMap: Map<string, (() => JSX.Element) | undefined>;
  customStatusMap: Map<string, (() => JSX.Element) | undefined>;

  initializeIcons: () => void;
  updateCustomStatusIcons: (customProjectStatuses: ProjectStatus[]) => void;
  getStatusIcon: (statusName: string) => (() => JSX.Element) | undefined;
  getStatusIconById: (statusId: string) => (() => JSX.Element) | undefined;
}

export const useStatusIconStore = create<StatusIconState>((set, get) => ({
  statusIconMap: new Map(),
  customStatusMap: new Map(),

  initializeIcons: () => {
    const { statusIconMap } = get();

    // Pre-calculate ALL default status icons ONCE at startup
    Object.values(KanbanDefaultStates).forEach((statusName) => {
      const defaultStatusKey = Object.keys(KanbanDefaultStates).find(
        (k) =>
          KanbanDefaultStates[k as keyof typeof KanbanDefaultStates] ===
          statusName,
      );

      if (defaultStatusKey) {
        const statusId = STATE_KEY_TO_STATUS_ID[defaultStatusKey] || "backlog";
        const statusObj = STATUS_LIST.find((s) => s.id === statusId);
        statusIconMap.set(statusName, statusObj?.icon);
      }
    });

    // Add backlog fallback
    const backlogStatus = STATUS_LIST.find((s) => s.id === "backlog");
    statusIconMap.set("", backlogStatus?.icon);
    statusIconMap.set("backlog", backlogStatus?.icon);
    statusIconMap.set("Backlog", backlogStatus?.icon);

    set({ statusIconMap: new Map(statusIconMap) });
  },

  updateCustomStatusIcons: (customProjectStatuses: ProjectStatus[]) => {
    const { statusIconMap } = get();
    const customMap = new Map();

    // Pre-calculate custom status icons and cache them
    customProjectStatuses.forEach((customStatus) => {
      let parentStatus = STATUS_LIST.find((s) => s.id === customStatus.type);

      if (!parentStatus) {
        const mappedType =
          STATUS_TYPE_MAPPING[
            customStatus.type as keyof typeof STATUS_TYPE_MAPPING
          ];
        if (mappedType) {
          parentStatus = STATUS_LIST.find((s) => s.id === mappedType);
        }
      }

      const icon = parentStatus?.icon || statusIconMap.get("backlog");
      customMap.set(customStatus.name, icon);
      customMap.set(customStatus.id, icon);
    });

    set({ customStatusMap: customMap });
  },

  getStatusIcon: (statusName: string) => {
    const { statusIconMap, customStatusMap } = get();

    const customIcon = customStatusMap.get(statusName);
    if (customIcon) return customIcon;

    const defaultIcon = statusIconMap.get(statusName);
    if (defaultIcon) return defaultIcon;

    return statusIconMap.get("backlog");
  },

  getStatusIconById: (statusId: string) => {
    const { customStatusMap } = get();
    return customStatusMap.get(statusId);
  },
}));
